import { Plugin } from '@nuxt/types'
import ROUTES from '~/config/routes'
import { ShopConfig } from '~/module/shopConfig/shopConfigPlugin'
import { Shops } from '~/config/shops'

/**
 * Client-only plugin: registers external routes (without component) into
 * vue-router so the routing middleware can detect them and redirect to
 * the monolith via window.location.href.
 *
 * This plugin MUST remain client-only (mode: 'client' in module/router/index.ts).
 * Running addRoutes on every SSR request caused unnecessary memory allocation
 * and GC pressure because:
 *  - Object.values().filter().map() with spread creates ~20+ temp objects per request
 *  - addRoutes() mutates the router matcher's internal pathList/pathMap/nameMap
 *  - The routing middleware (middleware/routing.ts) skips server entirely
 */
const router: Plugin = ({ app }) => {
  // Safety guard: this plugin should never run on server.
  // The module registers it with mode: 'client', but this guard protects
  // against accidental misconfiguration.
  if (process.server) {
    return
  }

  const { shopId } = app.$shopConfig as ShopConfig
  const currentLanguage: Shops = shopId

  // Collect routes WITHOUT components (external/monolith routes).
  // Routes WITH components are already registered at build time via extendRoutes.
  const externalRoutes = Object.values(ROUTES)
    .filter((routeConfig: any) => !routeConfig.component)
    .map(({ paths, aliases, ...routeConfig }: any) => {
      const currentLanguagePath = paths[currentLanguage]
      const path = currentLanguagePath ? `/${currentLanguagePath}` : ''
      const currentLanguageAlias = aliases && aliases[currentLanguage]
      const alias = currentLanguageAlias ? `/${currentLanguageAlias}` : ''

      return {
        ...routeConfig,
        alias,
        component: routeConfig.component,
        path,
      }
    })

  if (externalRoutes.length > 0) {
    // @ts-ignore - addRoutes is the public vue-router 3 API
    app.router.addRoutes(externalRoutes)
  }
}

export default router
